import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Search,
  DollarSign,
  ExternalLink,
  Calendar,
  Building2,
  Users,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import SaveGrantButton from "@/components/opportunities/SaveGrantButton";
import JourneyStep from "@/components/home/JourneyStep";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

type Grant = {
  title: string;
  source: string;
  source_type: string;
  amount: string;
  deadline: string;
  url: string;
  description: string;
  eligibility: string;
};

const AREAS = [
  { label: "Housing", icon: Building2, color: "bg-issue-housing/10 text-issue-housing border-issue-housing/30" },
  { label: "Climate", icon: Sparkles, color: "bg-issue-climate/10 text-issue-climate border-issue-climate/30" },
  { label: "Education", icon: Users, color: "bg-issue-education/10 text-issue-education border-issue-education/30" },
  { label: "Justice", icon: Search, color: "bg-issue-justice/10 text-issue-justice border-issue-justice/30" },
  { label: "Health", icon: Users, color: "bg-issue-health/10 text-issue-health border-issue-health/30" },
];

const sourceTypeColors: Record<string, string> = {
  Federal: "bg-primary/10 text-primary border-primary/20",
  State: "bg-accent/10 text-accent border-accent/20",
  Foundation: "bg-issue-justice/10 text-issue-justice border-issue-justice/20",
  Corporate: "bg-issue-climate/10 text-issue-climate border-issue-climate/20",
};

const GrantCard = ({ grant, index }: { grant: Grant; index: number }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="bg-card border border-border rounded-lg p-5 hover:border-primary/30 transition-all group"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h4 className="font-display font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
          {grant.title}
        </h4>
        <Badge
          variant="outline"
          className={`text-[10px] shrink-0 ${sourceTypeColors[grant.source_type] || "bg-muted text-muted-foreground"}`}
        >
          {grant.source_type}
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground mb-3">{grant.source}</p>

      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        <span className="flex items-center gap-1 text-accent">
          <DollarSign className="h-3 w-3" />
          {grant.amount}
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {grant.deadline}
        </span>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="text-xs text-muted-foreground mb-2">{grant.description}</p>
            <p className="text-xs text-muted-foreground mb-3">
              <span className="text-foreground font-medium">Eligibility:</span> {grant.eligibility}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between pt-2 border-t border-border gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Less" : "Details"}
        </button>
        {grant.url && (
          <a
            href={grant.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            Apply <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="mt-2 flex justify-end">
        <SaveGrantButton
          grant={{
            title: grant.title,
            source: grant.source,
            source_type: grant.source_type,
            amount: grant.amount,
            deadline: grant.deadline,
            url: grant.url,
            description: grant.description,
          }}
        />
      </div>
    </motion.div>
  );
};

const GrantFinder = () => {
  const [selectedArea, setSelectedArea] = useState<string | null>("Health");
  const { user } = useAuth();

  const { data: legislation } = useQuery({
    queryKey: ["legislation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("*")
        .order("date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const {
    data: grantResult,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["grants", selectedArea],
    queryFn: async () => {
      if (!selectedArea) return null;

      // Find a relevant bill to provide context
      const relevantBill = legislation?.find((b) => b.area === selectedArea);

      const { data, error } = await supabase.functions.invoke("find-grants", {
        body: {
          area: selectedArea,
          legislationTitle: relevantBill?.title,
          legislationDescription: relevantBill?.description,
        },
      });

      if (error) throw error;
      return data as { success: boolean; grants: Grant[]; area: string };
    },
    enabled: !!selectedArea && !!user,
    staleTime: 5 * 60 * 1000, // cache for 5 mins
  });

  return (
    <JourneyStep
      step="05"
      label="Economic Development"
      tone="muted"
      title={<>How the <span className="text-gradient-primary">dollars</span> move</>}
      subhead="Our economic development practice maps federal, state, and philanthropic capital to Alabama priorities. Pick a focus area and watch our AI engine surface live funding our clients are positioned to win."
    >
        {/* Area Selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {AREAS.map(({ label, icon: Icon, color }) => (
            <motion.button
              key={label}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedArea(selectedArea === label ? null : label)}
              className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                selectedArea === label
                  ? `${color} shadow-glow`
                  : "bg-card border-border hover:border-primary/30 text-muted-foreground"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="font-display font-semibold text-sm">{label}</span>
            </motion.button>
          ))}
        </div>

        {/* Results */}
        {!selectedArea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 text-muted-foreground"
          >
            <Sparkles className="h-10 w-10 mx-auto mb-4 text-primary/40" />
            <p className="font-display text-lg">Select a focus area to discover grants</p>
            <p className="text-sm mt-2">
              Our AI analyzes Alabama legislation to match relevant funding opportunities
            </p>
          </motion.div>
        )}

        {!user && selectedArea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 border border-border rounded-lg bg-card/50"
          >
            <Sparkles className="h-10 w-10 mx-auto mb-4 text-primary/40" />
            <p className="font-display text-lg mb-2">Sign in to run a live grant search</p>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Live AI grant matching is a member feature. Create a free account to surface
              current funding opportunities for {selectedArea} in Alabama.
            </p>
            <Button asChild>
              <Link to="/auth">Sign in or create an account</Link>
            </Button>
          </motion.div>
        )}

        {user && (isLoading || isFetching) && selectedArea && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-5 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex gap-3">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        )}

        {user && grantResult?.success && !isFetching && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Badge variant="secondary" className="text-xs">
                {grantResult.grants?.length || 0} grants found
              </Badge>
              <span className="text-xs text-muted-foreground">
                for {grantResult.area} in Alabama
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grantResult.grants?.map((grant: Grant, i: number) => (
                <GrantCard key={`${grant.title}-${i}`} grant={grant} index={i} />
              ))}
            </div>
          </div>
        )}
    </JourneyStep>
  );
};

export default GrantFinder;
